import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../api";
import { IUploadContentData, IFetchContentParams, IUpdateContentData } from "./types";
import axios from "axios";
import { setUploadProgress, setUploadAbortController } from "./slice";
import { UploadContentErrorResponse, UploadContentSuccessResponse } from "./types";

const MB = 1024 * 1024;
const GB = 1024 * MB;

const splitFileIntoChunks = (file: File): Blob[] => {
    let chunkSize: number;
    if (file.size <= 500 * MB) chunkSize = 10 * MB;
    else if (file.size <= 2 * GB) chunkSize = 25 * MB;
    else if (file.size <= 10 * GB) chunkSize = 50 * MB;
    else if (file.size <= 50 * GB) chunkSize = 100 * MB;
    else chunkSize = 200 * MB;

    const chunks: Blob[] = [];
    let offset = 0;

    while (offset < file.size) {
        chunks.push(file.slice(offset, offset + chunkSize));
        offset += chunkSize;
    }
    return chunks;
};

export const uploadContent = createAsyncThunk<
    UploadContentSuccessResponse,
    IUploadContentData,
    { rejectValue: UploadContentErrorResponse }
>(
    "listIcon/uploadContent",
    async (data: IUploadContentData, { rejectWithValue, dispatch, signal }) => {
        const abortController = new AbortController();
        dispatch(setUploadAbortController(abortController));
        signal.addEventListener("abort", () => abortController.abort());

        try {
            const fileToUpload = data.file as File;
            const chunks = splitFileIntoChunks(fileToUpload);

            // 1. Create multipart upload
            const initResponse = await api.post("/content-management/multipart/start", {
                    fileName: fileToUpload.name,
                    contentType: fileToUpload.type,
                },
            );
            const { uploadId, key } = initResponse.data;
            const uploadedParts: { ETag: string; PartNumber: number }[] = [];
            let uploadedBytes = 0;
            const totalBytes = fileToUpload.size;
            // 2. Upload parallel chunk
            const PARALLEL_UPLOADS = 4;
            for (let i = 0; i < chunks.length; i += PARALLEL_UPLOADS) {
                const batch = chunks.slice(i, i + PARALLEL_UPLOADS);
                const batchPromises = batch.map(async (chunk, index) => {
                    const partNumber = i + index + 1;
                    const presignResponse = await api.post("/content-management/multipart/presign", {
                            key,
                            uploadId,
                            partNumber,
                        },
                    );
                    const { signedUrl } = presignResponse.data;
                    let chunkUploadedBytes = 0;

                    const uploadResponse = await axios.put(signedUrl, chunk, {
                        headers: { "Content-Type": fileToUpload.type },
                        signal: abortController.signal,
                        onUploadProgress: (progressEvent) => {
                            const currentLoaded = progressEvent.loaded - chunkUploadedBytes;
                            chunkUploadedBytes = progressEvent.loaded;
                            uploadedBytes += currentLoaded;
                            const percent = Math.round((uploadedBytes / totalBytes) * 100);
                            dispatch(setUploadProgress(percent));
                        },
                    });
                    return {
                        ETag: uploadResponse.headers.etag,
                        PartNumber: partNumber,
                    };
                });
                const batchResults = await Promise.all(batchPromises);
                uploadedParts.push(...batchResults);
            }

            // 3. Complete upload
            const completeResponse = await api.post("/content-management/multipart/complete",{
                    key,
                    uploadId,
                    parts: uploadedParts,
                },
            );
            const fileUrl = completeResponse.data;

            // 4. Send content data to backend
            const { file: _, ...restData } = data;
            const finalPayload = {
                ...restData,
                fileUrl: fileUrl.key,
            };

            const response = await api.post("/content-management/add-content", finalPayload);
            dispatch(setUploadAbortController(null));
            dispatch(setUploadProgress(0));
            return response.data;
        } catch (error: any) {
            dispatch(setUploadProgress(0));
            if (axios.isCancel(error)) {
                throw new DOMException("Upload aborted by user", "AbortError"); // Re-throw AbortError
            }
            return rejectWithValue(error?.response?.data || { message: "An unexpected error occurred" });
        }
    },
);

export const fetchContentList = createAsyncThunk(
    "listIcon/fetchContentList",
    async (params: IFetchContentParams, { rejectWithValue }) => {
        try {
            const response = await api.get("/content-management/get-content", {
                params: params,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue( error?.response?.data || { message: "An unexpected error occurred" });
        }
    },
);

export const fetchContentById = createAsyncThunk(
    "listIcon/fetchContentById",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await api.get(`/content-management/get-content/${id}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue( error?.response?.data || { message: "An unexpected error occurred" });
        }
    },
);

export const updateContent = createAsyncThunk(
    "listIcon/updateContent",
    async (data: IUpdateContentData, { rejectWithValue }) => {
        try {
            const { id, ...cleanData } = data;
            const response = await api.patch(`/content-management/${id}`, cleanData);
            return response.data;
        } catch (error: any) {
            return rejectWithValue( error?.response?.data || { message: "An unexpected error occurred" });
        }
    },
);

export const deleteContent = createAsyncThunk(
    "listIcon/deleteContent",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/content-management/${id}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue( error?.response?.data || { message: "An unexpected error occurred" });
        }
    },
);
